"use client";

import {
  clsx,
  raise,
  matchKey,
  useLocale,
  useMountEffect,
  useRef,
  type Millis,
} from "@/ui";


const TrustpilotReview = process.env.NEXT_PUBLIC_TRUSTPILOT_REVIEW || raise("Missing NEXT_PUBLIC_TRUSTPILOT_REVIEW");
const TrustpilotTemplateId = process.env.NEXT_PUBLIC_TRUSTPILOT_TEMPLATE_ID || raise("Missing NEXT_PUBLIC_TRUSTPILOT_TEMPLATE_ID");
const TrustpilotBusinessUnitId = process.env.NEXT_PUBLIC_TRUSTPILOT_BUSINESS_UNIT_ID || raise("Missing NEXT_PUBLIC_TRUSTPILOT_BUSINESS_UNIT_ID");

const ScriptPollingIntervalFast: Millis = 100;
const ScriptPollingIntervalSlow: Millis = 1_000;
const ScriptPollingSlowdownAfter: Millis = 3_000;


export function TrustpilotWidget(props: {
  className?: string;
  height?: `${number}px`;
  width?: `${number}${"px" | "%"}`;
  theme?: "light" | "dark";
}) {
  const ref = useRef<HTMLDivElement>(null);
  const data = useWidgetData();
  const theme = props.theme ?? "dark";
  const width = props.width ?? "100%";
  const height = props.height ?? "150px";

  useMountEffect(() => {
    const startTime = Date.now();
    let isMounted = true;

    const activateWidgetOrRetry = () => {
      if (!isMounted) {
        return;
      }

      if (window.Trustpilot && window.Trustpilot.loadFromElement && ref.current) {
        window.Trustpilot.loadFromElement(ref.current, true);
        return;
      }

      setTimeout(activateWidgetOrRetry, (
        (Date.now() - startTime) < ScriptPollingSlowdownAfter
          ? ScriptPollingIntervalFast
          : ScriptPollingIntervalSlow
      ));
    };

    activateWidgetOrRetry();

    return () => {
      isMounted = false;
    };
  });

  return (
    <div
      ref={ref}
      className={clsx("trustpilot-widget", props.className)}
      data-locale={data.locale}
      data-template-id={data.templateId}
      data-businessunit-id={data.businessUnitId}
      data-style-height={height}
      data-style-width={width}
      data-theme={theme}
      style={{ width, height }}
    >
      <TrustpilotLink hidden href={data.href} />
    </div>
  );
}


function TrustpilotLink(props: {
  hidden?: boolean;
  href: string;
}) {
  return (
    <a
      className={props.hidden ? "opacity-0" : ""}
      href={props.href}
      target="_blank"
      rel="noopener"
    >
      Trustpilot
    </a>
  );
}


function useWidgetData() {
  const pageLocale = useLocale();

  const locale = matchKey(pageLocale, {
    "en": () => "en-GB", // or `en-US`
    "fr": () => "fr-FR",
  });

  const subDomain = matchKey(pageLocale, {
    "en": () => "www", // or `uk`, but better use US/International `www`
    "fr": () => "fr",
  });

  const href = `https://${subDomain}.trustpilot.com/review/${TrustpilotReview}`;
  const templateId = TrustpilotTemplateId;
  const businessUnitId = TrustpilotBusinessUnitId;

  return {
    href,
    locale,
    templateId,
    businessUnitId,
  };
}
